import { useState } from 'react';
import type { ReactNode } from 'react';
import { useApi, useConsole, useRead } from './context';
import { Empty, Failure, Loading, When } from './bits';
import { Pager } from './Fleet';

/**
 * The trail, across tenants — plain, fast, and greppable-feeling on purpose.
 *
 * This screen is a log viewer and should look like one: monospace where it
 * matters, one row per action, filters that are text boxes rather than pickers,
 * and no chrome between the operator and the line they are hunting. The thing
 * being read is a record of what people did to other people's businesses, and
 * the useful shape for that is the shape `grep` has.
 *
 * **Reading it is itself an audited action.** `admin-api` writes an
 * `audit.view` row after every read, with the filter it used and the number of
 * rows it returned; the row is written after the read, so an `audit.view` never
 * appears in its own result and two reads in a row show the first one. The note
 * at the top says so, because an operator surprised by their own name in the
 * list should find the explanation before they go looking for a bug.
 *
 * `tenant_id` is null on genuinely cross-tenant work — a fleet listing is about
 * no tenant in particular — and that null is rendered as `fleet`, not as a gap.
 */
export function Audit({ tenantId }: { tenantId?: string | undefined }): ReactNode {
  const api = useApi();
  const { config } = useConsole();

  const [tenant, setTenant] = useState(tenantId ?? '');
  const [action, setAction] = useState('');
  const [actor, setActor] = useState('');
  const [since, setSince] = useState('');
  const [offset, setOffset] = useState(0);
  const [applied, setApplied] = useState(0);

  const page = useRead(
    () =>
      api.audit({
        ...(tenant === '' ? {} : { tenant_id: tenant }),
        ...(action === '' ? {} : { action }),
        ...(actor === '' ? {} : { actor }),
        ...(since === '' ? {} : { since: isoOf(since) }),
        limit: config.pageSize,
        offset,
      }),
    [offset, applied, config.pageSize],
  );

  const search = (): void => {
    setOffset(0);
    setApplied((n) => n + 1);
  };

  return (
    <div className="view">
      <header className="view__head">
        <h2>Audit</h2>
      </header>

      <form
        className="filters"
        onSubmit={(e) => {
          e.preventDefault();
          search();
        }}
      >
        <label className="inline-field">
          <span>Tenant</span>
          <input
            value={tenant}
            onChange={(e) => setTenant(e.target.value)}
            placeholder="t_lovespa_frenchtown"
          />
        </label>
        <label className="inline-field">
          <span>Action</span>
          <input
            value={action}
            onChange={(e) => setAction(e.target.value)}
            placeholder="key.issue"
          />
        </label>
        <label className="inline-field">
          <span>Actor</span>
          <input
            value={actor}
            onChange={(e) => setActor(e.target.value)}
            placeholder="operator:jane@jswaves.example"
          />
        </label>
        <label className="inline-field">
          <span>Since</span>
          <input
            type="datetime-local"
            value={since}
            onChange={(e) => setSince(e.target.value)}
            aria-label="Since"
          />
        </label>
        <button type="submit" className="button button--primary">
          Search
        </button>
      </form>

      <p className="muted">Reading the trail is itself an audit_log row (spec §6.3).</p>

      {page.failure !== null ? <Failure failure={page.failure} /> : null}

      {page.value === null ? (
        page.loading ? (
          <Loading what="the trail" />
        ) : null
      ) : page.value.entries.length === 0 ? (
        <Empty>No entries match.</Empty>
      ) : (
        <>
          <table className="table table--log">
            <thead>
              <tr>
                <th>When</th>
                <th>Tenant</th>
                <th>Actor</th>
                <th>Action</th>
                <th>Target</th>
                <th>Detail</th>
              </tr>
            </thead>
            <tbody>
              {page.value.entries.map((e) => (
                <tr key={e.audit_id}>
                  <td>
                    <When iso={e.at} />
                  </td>
                  <td>
                    {e.tenant_id === null ? (
                      <span className="muted">fleet</span>
                    ) : (
                      <code>{e.tenant_id}</code>
                    )}
                  </td>
                  <td>{e.actor}</td>
                  <td>
                    <code>{e.action}</code>
                  </td>
                  <td className="muted">{e.target}</td>
                  <td className="detail-cell">{summarize(e.detail)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <Pager
            count={page.value.entries.length}
            limit={page.value.limit}
            offset={page.value.offset}
            busy={page.loading}
            onOffset={setOffset}
          />
        </>
      )}
    </div>
  );
}

/**
 * The detail column, as `key=value` pairs on one line.
 *
 * Pretty-printed JSON would make every row four lines tall and the log
 * unscannable; the detail objects `admin-api` writes are small and flat by
 * design (`repos/audit.ts`), so one line each is honest rather than lossy.
 */
function summarize(detail: Record<string, unknown>): string {
  return Object.entries(detail)
    .map(([k, v]) => `${k}=${typeof v === 'object' && v !== null ? JSON.stringify(v) : String(v)}`)
    .join(' ');
}

function isoOf(local: string): string {
  const parsed = new Date(local);
  return Number.isNaN(parsed.getTime()) ? local : parsed.toISOString();
}
